import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";

import { curveImportFieldHelp } from "../../app/guidance/fieldHelpRegistry";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";

import type {
  AxisQuantity,
  CurveDraftDocument,
  CurveKind,
  CurveTraceDraft,
} from "./types";

export interface SimpleCurveDataEditorProps {
  busy?: boolean;
  disabled?: boolean;
  document: CurveDraftDocument;
  onCheck: (nextDocument: CurveDraftDocument) => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
  suggestedRpms?: number[];
}

interface EntryRow {
  flow: string;
  id: string;
  pointId: string | null;
  value: string;
}

interface CurveTableDefinition {
  kind: "efficiency" | "brake_power" | "npshr";
  label: string;
  quantity: AxisQuantity;
  unitLabel: string;
  units: ReadonlyArray<{ label: string; value: string }>;
}

interface ParsedPoint {
  flow: number;
  pointId: string | null;
  value: number;
}

const FLOW_UNITS = [
  { label: "gpm", value: "gpm" },
  { label: "m³/s", value: "m3/s" },
  { label: "m³/h", value: "m3/h" },
  { label: "L/s", value: "l/s" },
  { label: "bbl/min", value: "bpm" },
] as const;

const HEAD_UNITS = [
  { label: "ft", value: "ft" },
  { label: "m", value: "m" },
] as const;

const OPTIONAL_TABLES: readonly CurveTableDefinition[] = [
  {
    kind: "efficiency",
    label: "Efficiency",
    quantity: "efficiency",
    unitLabel: "Efficiency unit",
    units: [
      { label: "%", value: "percent" },
      { label: "fraction", value: "fraction" },
    ],
  },
  {
    kind: "brake_power",
    label: "Brake power",
    quantity: "power",
    unitLabel: "Brake power unit",
    units: [
      { label: "hp", value: "hp" },
      { label: "kW", value: "kw" },
      { label: "W", value: "w" },
    ],
  },
  {
    kind: "npshr",
    label: "NPSHr",
    quantity: "npshr",
    unitLabel: "NPSHr unit",
    units: HEAD_UNITS,
  },
] as const;

const DEFAULT_OPTIONAL_UNITS: Record<CurveTableDefinition["kind"], string> = {
  brake_power: "hp",
  efficiency: "percent",
  npshr: "ft",
};

function blankRows(prefix: string, count = 3): EntryRow[] {
  return Array.from({ length: count }, (_, index) => ({
    flow: "",
    id: prefix + "-" + index,
    pointId: null,
    value: "",
  }));
}

function directHeadTraceIssue(trace: CurveTraceDraft): string | null {
  const calibration = trace.calibration_override;
  if (calibration === null)
    return "it has no trace-level source-unit calibration";
  if (
    calibration.x_axis.quantity !== "flow" ||
    calibration.y_axis.quantity !== "head"
  ) {
    return "its trace calibration does not describe flow and head";
  }
  if (
    !FLOW_UNITS.some(({ value }) => value === calibration.x_axis.unit) ||
    !HEAD_UNITS.some(({ value }) => value === calibration.y_axis.unit)
  ) {
    return "its source units are not supported by this form";
  }
  if (trace.points.length < 2) return "it has fewer than two points";
  if (
    trace.points.some((point) => point.source === null || point.pixel !== null)
  ) {
    return "it contains pixel-based points instead of direct source-unit values";
  }
  const sources = trace.points.map((point) => point.source!);
  if (
    sources.some(
      (point) =>
        !Number.isFinite(point.x) ||
        !Number.isFinite(point.y) ||
        point.x < 0 ||
        point.y < 0,
    )
  ) {
    return "it contains invalid source-unit values";
  }
  if (new Set(sources.map((point) => point.x)).size !== sources.length) {
    return "it contains repeated flow values";
  }
  return null;
}

function initialHeadState(document: CurveDraftDocument) {
  const heads = document.traces.filter((trace) => trace.kind === "head");
  const candidate = heads[0];
  const trace =
    heads.length === 1 &&
    candidate !== undefined &&
    directHeadTraceIssue(candidate) === null
      ? candidate
      : null;
  if (trace === null) {
    return {
      flowUnit: "gpm",
      headUnit: "ft",
      rows: blankRows("head"),
      rpm: "",
    };
  }
  const points: EntryRow[] = [...trace.points]
    .sort((left, right) => (left.source?.x ?? 0) - (right.source?.x ?? 0))
    .map((point, index) => ({
      flow: String(point.source?.x ?? ""),
      id: "head-" + index,
      pointId: point.id,
      value: String(point.source?.y ?? ""),
    }));
  while (points.length < 3) {
    points.push({
      flow: "",
      id: "head-" + points.length,
      pointId: null,
      value: "",
    });
  }
  return {
    flowUnit: trace.calibration_override?.x_axis.unit ?? "gpm",
    headUnit: trace.calibration_override?.y_axis.unit ?? "ft",
    rows: points,
    rpm: trace.speed_rpm === null ? "" : String(trace.speed_rpm),
  };
}

function parseRows(rows: EntryRow[], label: string): ParsedPoint[] | string {
  const started = rows.filter(
    (row) => row.flow.trim() !== "" || row.value.trim() !== "",
  );
  if (
    started.some((row) => row.flow.trim() === "" || row.value.trim() === "")
  ) {
    return `${label}: complete both cells in every started row.`;
  }
  if (started.length < 2) {
    return `${label}: enter at least two complete rows.`;
  }
  const parsed = started.map((row) => ({
    flow: Number(row.flow),
    pointId: row.pointId,
    value: Number(row.value),
  }));
  if (
    parsed.some(
      (point) =>
        !Number.isFinite(point.flow) ||
        !Number.isFinite(point.value) ||
        point.flow < 0 ||
        point.value < 0,
    )
  ) {
    return `${label}: flow and value must be finite, nonnegative numbers.`;
  }
  if (new Set(parsed.map((point) => point.flow)).size !== parsed.length) {
    return `${label}: each flow value must be unique.`;
  }
  return parsed.sort((left, right) => left.flow - right.flow);
}

function axis(quantity: AxisQuantity, unit: string, values: number[]) {
  const low = Math.min(...values);
  const maximum = Math.max(...values);
  const high =
    maximum === low ? low + Math.max(Math.abs(low) * 0.01, 1) : maximum;
  return {
    direction: "increasing" as const,
    pixel_end: 1,
    pixel_start: 0,
    quantity,
    scale: "linear" as const,
    unit,
    value_end: high,
    value_start: low,
  };
}

function directTrace(
  kind: CurveKind,
  label: string,
  rpm: number,
  flowUnit: string,
  valueUnit: string,
  quantity: AxisQuantity,
  points: ParsedPoint[],
  existingTrace?: CurveTraceDraft,
): CurveTraceDraft {
  const base: CurveTraceDraft = existingTrace ?? {
    calibration_override: null,
    chart_region: null,
    contour_unit: null,
    contour_value: null,
    id: globalThis.crypto.randomUUID(),
    kind,
    label,
    origin: "measured",
    page_number: null,
    points: [],
    speed_rpm: rpm,
    suggested_by: null,
    suggestion_confidence: null,
    trim_label: null,
  };
  return {
    ...base,
    calibration_override: {
      x_axis: axis(
        "flow",
        flowUnit,
        points.map((point) => point.flow),
      ),
      y_axis: axis(
        quantity,
        valueUnit,
        points.map((point) => point.value),
      ),
    },
    kind,
    label: existingTrace?.label ?? label,
    points: points.map((point, sequence) => ({
      id: point.pointId ?? globalThis.crypto.randomUUID(),
      pixel: null,
      sequence,
      source: { x: point.flow, y: point.value },
    })),
    speed_rpm: rpm,
  };
}

interface DataTableProps {
  disabled: boolean;
  rows: EntryRow[];
  title: string;
  valueHeading: string;
  onAdd: () => void;
  onChange: (id: string, coordinate: "flow" | "value", value: string) => void;
  onRemove: (id: string) => void;
}

function DataTable({
  disabled,
  rows,
  title,
  valueHeading,
  onAdd,
  onChange,
  onRemove,
}: DataTableProps) {
  return (
    <div className="curve-data-table">
      <table>
        <caption className="visually-hidden">{title} data</caption>
        <thead>
          <tr>
            <th scope="col">Flow</th>
            <th scope="col">{valueHeading}</th>
            <th scope="col">
              <span className="visually-hidden">Row actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id}>
              <td>
                <input
                  aria-label={`${title} flow row ${index + 1}`}
                  aria-describedby="simple-curve-entry-help"
                  data-guided-exempt="simple-curve-table-step-guidance"
                  disabled={disabled}
                  inputMode="decimal"
                  min="0"
                  step="any"
                  type="number"
                  value={row.flow}
                  onChange={(event) =>
                    onChange(row.id, "flow", event.target.value)
                  }
                />
              </td>
              <td>
                <input
                  aria-label={`${title} ${valueHeading.toLocaleLowerCase()} row ${index + 1}`}
                  aria-describedby="simple-curve-entry-help"
                  data-guided-exempt="simple-curve-table-step-guidance"
                  disabled={disabled}
                  inputMode="decimal"
                  min="0"
                  step="any"
                  type="number"
                  value={row.value}
                  onChange={(event) =>
                    onChange(row.id, "value", event.target.value)
                  }
                />
              </td>
              <td>
                <button
                  aria-label={`Remove ${title} row ${index + 1}`}
                  disabled={disabled}
                  type="button"
                  onClick={() => onRemove(row.id)}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        aria-label={`Add ${title} row`}
        disabled={disabled}
        type="button"
        onClick={onAdd}
      >
        Add row
      </button>
    </div>
  );
}

export function SimpleCurveDataEditor({
  busy = false,
  disabled = false,
  document,
  onCheck,
  onDirtyChange,
  suggestedRpms = [],
}: SimpleCurveDataEditorProps) {
  const initial = initialHeadState(document);
  const [rpm, setRpm] = useState(initial.rpm);
  const [flowUnit, setFlowUnit] = useState(initial.flowUnit);
  const [headUnit, setHeadUnit] = useState(initial.headUnit);
  const [headRows, setHeadRows] = useState(initial.rows);
  const [optionalRows, setOptionalRows] = useState(
    () =>
      Object.fromEntries(
        OPTIONAL_TABLES.map((table) => [table.kind, blankRows(table.kind)]),
      ) as Record<CurveTableDefinition["kind"], EntryRow[]>,
  );
  const [optionalUnits, setOptionalUnits] = useState(DEFAULT_OPTIONAL_UNITS);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const dirtyRef = useRef(false);
  const nextRowId = useRef(Math.max(3, initial.rows.length));
  const previousDocument = useRef(document);

  useEffect(() => {
    if (previousDocument.current === document) return;
    previousDocument.current = document;
    const next = initialHeadState(document);
    setRpm(next.rpm);
    setFlowUnit(next.flowUnit);
    setHeadUnit(next.headUnit);
    setHeadRows(next.rows);
    setOptionalRows(
      Object.fromEntries(
        OPTIONAL_TABLES.map((table) => [table.kind, blankRows(table.kind)]),
      ) as Record<CurveTableDefinition["kind"], EntryRow[]>,
    );
    setOptionalUnits(DEFAULT_OPTIONAL_UNITS);
    setMessage("");
    nextRowId.current = Math.max(3, next.rows.length);
    if (dirtyRef.current) {
      dirtyRef.current = false;
      onDirtyChange?.(false);
    }
  }, [document, onDirtyChange]);

  const headTraces = document.traces.filter((trace) => trace.kind === "head");
  const singleHeadTrace =
    headTraces.length === 1 ? (headTraces[0] ?? null) : null;
  const incompatibleHeadIssue =
    singleHeadTrace === null ? null : directHeadTraceIssue(singleHeadTrace);
  const editorBlockMessage =
    headTraces.length > 1
      ? "This draft has multiple head traces. Use Advanced curve tools to choose which trace to change."
      : incompatibleHeadIssue === null
        ? null
        : "This head trace cannot be safely represented here because " +
          incompatibleHeadIssue +
          ". Use Advanced curve tools to edit it.";
  const compatibleHeadTrace =
    singleHeadTrace !== null && incompatibleHeadIssue === null
      ? singleHeadTrace
      : null;
  const locked = disabled || busy || submitting || editorBlockMessage !== null;
  const rpmSuggestions = useMemo(
    () => [
      ...new Set(
        suggestedRpms.filter((value) => Number.isFinite(value) && value > 0),
      ),
    ],
    [suggestedRpms],
  );

  const markDirty = () => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    onDirtyChange?.(true);
  };

  const updateRows = (
    rows: EntryRow[],
    setRows: (next: EntryRow[]) => void,
    id: string,
    coordinate: "flow" | "value",
    value: string,
  ) => {
    setRows(
      rows.map((row) =>
        row.id === id ? { ...row, [coordinate]: value } : row,
      ),
    );
    markDirty();
  };

  const previewPoints = useMemo(() => {
    const parsed = headRows
      .filter((row) => row.flow !== "" && row.value !== "")
      .map((row) => ({ flow: Number(row.flow), value: Number(row.value) }))
      .filter(
        (point) => Number.isFinite(point.flow) && Number.isFinite(point.value),
      );
    if (parsed.length < 2) return "";
    const minX = Math.min(...parsed.map((point) => point.flow));
    const maxX = Math.max(...parsed.map((point) => point.flow));
    const minY = Math.min(...parsed.map((point) => point.value));
    const maxY = Math.max(...parsed.map((point) => point.value));
    if (minX === maxX) return "";
    return parsed
      .sort((left, right) => left.flow - right.flow)
      .map((point) => {
        const x = 10 + ((point.flow - minX) / (maxX - minX)) * 180;
        const y =
          maxY === minY ? 50 : 90 - ((point.value - minY) / (maxY - minY)) * 80;
        return `${x},${y}`;
      })
      .join(" ");
  }, [headRows]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setMessage("");
    if (editorBlockMessage !== null) {
      setMessage(editorBlockMessage);
      return;
    }
    const speed = Number(rpm);
    if (
      rpm.trim() === "" ||
      !Number.isFinite(speed) ||
      speed <= 0 ||
      !Number.isInteger(speed)
    ) {
      setMessage("Tested RPM must be a whole number greater than 0.");
      return;
    }
    const parsedHead = parseRows(headRows, "Head curve");
    if (typeof parsedHead === "string") {
      setMessage(parsedHead);
      return;
    }

    const additions: CurveTraceDraft[] = [];
    for (const table of OPTIONAL_TABLES) {
      const rows = optionalRows[table.kind];
      const hasValues = rows.some(
        (row) => row.flow.trim() !== "" || row.value.trim() !== "",
      );
      if (!hasValues) continue;
      if (document.traces.some((trace) => trace.kind === table.kind)) {
        setMessage(
          `This draft already has a ${table.label} trace. Use Advanced editing to change it.`,
        );
        return;
      }
      const parsed = parseRows(rows, table.label);
      if (typeof parsed === "string") {
        setMessage(parsed);
        return;
      }
      if (
        table.kind === "efficiency" &&
        parsed.some((point) =>
          optionalUnits.efficiency === "percent"
            ? point.value > 100
            : point.value > 1,
        )
      ) {
        setMessage(
          "Efficiency values must be at most 100 percent or 1 as a fraction.",
        );
        return;
      }
      additions.push(
        directTrace(
          table.kind,
          table.label,
          speed,
          flowUnit,
          optionalUnits[table.kind],
          table.quantity,
          parsed,
        ),
      );
    }

    const nextHead = directTrace(
      "head",
      "Head",
      speed,
      flowUnit,
      headUnit,
      "head",
      parsedHead,
      compatibleHeadTrace ?? undefined,
    );
    const tracesWithHead =
      compatibleHeadTrace === null
        ? [...document.traces, nextHead]
        : document.traces.map((trace) =>
            trace.id === compatibleHeadTrace.id ? nextHead : trace,
          );
    const nextDocument: CurveDraftDocument = {
      ...document,
      traces: [...tracesWithHead, ...additions],
    };
    setSubmitting(true);
    try {
      await onCheck(nextDocument);
      if (dirtyRef.current) {
        dirtyRef.current = false;
        onDirtyChange?.(false);
      }
    } catch (error: unknown) {
      setMessage(
        error instanceof Error
          ? error.message
          : "The curve could not be checked.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      aria-labelledby="simple-curve-data-title"
      className="curve-simple-entry"
    >
      <h2 id="simple-curve-data-title">2. Enter the pump curve</h2>
      <p id="simple-curve-entry-help">
        Enter each curve in the units printed on the source. A head-only curve
        can be stored, but power and suction checks will remain incomplete.
      </p>
      {editorBlockMessage === null ? null : (
        <p className="curve-alert" role="alert">
          {editorBlockMessage}
        </p>
      )}
      <form className="curve-simple-entry__form" noValidate onSubmit={submit}>
        <label>
          Tested RPM
          <input
            {...guided(curveImportFieldHelp.traceSpeed)}
            aria-describedby="tested-rpm-help"
            disabled={locked}
            list="simple-curve-rpm-suggestions"
            min="1"
            required
            step="1"
            type="number"
            value={rpm}
            onChange={(event) => {
              setRpm(event.target.value);
              markDirty();
            }}
          />
        </label>
        <datalist id="simple-curve-rpm-suggestions">
          {rpmSuggestions.map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        <p id="tested-rpm-help">
          Use the speed at which these values were tested.
        </p>

        <label>
          Flow unit
          <select
            {...guided(curveImportFieldHelp.axisUnit, "simple-flow-unit")}
            disabled={locked}
            value={flowUnit}
            onChange={(event) => {
              setFlowUnit(event.target.value);
              markDirty();
            }}
          >
            {FLOW_UNITS.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Head unit
          <select
            {...guided(curveImportFieldHelp.axisUnit, "simple-head-unit")}
            disabled={locked}
            value={headUnit}
            onChange={(event) => {
              setHeadUnit(event.target.value);
              markDirty();
            }}
          >
            {HEAD_UNITS.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </label>

        <DataTable
          disabled={locked}
          rows={headRows}
          title="Head curve"
          valueHeading="Head"
          onAdd={() => {
            setHeadRows([
              ...headRows,
              {
                flow: "",
                id: "head-" + nextRowId.current++,
                pointId: null,
                value: "",
              },
            ]);
            markDirty();
          }}
          onChange={(id, coordinate, value) =>
            updateRows(headRows, setHeadRows, id, coordinate, value)
          }
          onRemove={(id) => {
            setHeadRows(headRows.filter((row) => row.id !== id));
            markDirty();
          }}
        />

        {previewPoints === "" ? null : (
          <svg
            aria-label="Head curve preview"
            className="curve-simple-preview"
            role="img"
            viewBox="0 0 200 100"
            width="300"
          >
            <title>Head curve preview</title>
            <line stroke="currentColor" x1="10" x2="10" y1="10" y2="90" />
            <line stroke="currentColor" x1="10" x2="190" y1="90" y2="90" />
            <polyline
              fill="none"
              points={previewPoints}
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        )}

        <div className="curve-optional-data">
          <h3>Optional performance data</h3>
          {OPTIONAL_TABLES.map((table) => (
            <details key={table.kind}>
              <summary>{table.label}</summary>
              <label>
                {table.unitLabel}
                <select
                  {...guided(
                    curveImportFieldHelp.axisUnit,
                    `simple-${table.kind}-unit`,
                  )}
                  disabled={locked}
                  value={optionalUnits[table.kind]}
                  onChange={(event) => {
                    setOptionalUnits({
                      ...optionalUnits,
                      [table.kind]: event.target.value,
                    });
                    markDirty();
                  }}
                >
                  {table.units.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </label>
              <DataTable
                disabled={locked}
                rows={optionalRows[table.kind]}
                title={table.label}
                valueHeading={table.label}
                onAdd={() => {
                  setOptionalRows({
                    ...optionalRows,
                    [table.kind]: [
                      ...optionalRows[table.kind],
                      {
                        flow: "",
                        id: table.kind + "-" + nextRowId.current++,
                        pointId: null,
                        value: "",
                      },
                    ],
                  });
                  markDirty();
                }}
                onChange={(id, coordinate, value) =>
                  updateRows(
                    optionalRows[table.kind],
                    (next) =>
                      setOptionalRows({ ...optionalRows, [table.kind]: next }),
                    id,
                    coordinate,
                    value,
                  )
                }
                onRemove={(id) => {
                  setOptionalRows({
                    ...optionalRows,
                    [table.kind]: optionalRows[table.kind].filter(
                      (row) => row.id !== id,
                    ),
                  });
                  markDirty();
                }}
              />
            </details>
          ))}
        </div>

        {message === "" ? null : (
          <p className="curve-alert" role="alert">
            {message}
          </p>
        )}
        <button disabled={locked} type="submit">
          {busy || submitting ? "Checking…" : "Check curve"}
        </button>
      </form>
    </section>
  );
}
